import React, { useState, useEffect } from 'react';
import { FormHelperText, Grid } from '@mui/material';
import FormDialog from '../Common/FormDialog';
import { uploadImageToImgBB } from '../../utils/imageUpload';
import { Property, PropertyType, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useUpdateProperty, useAgents, useProjects } from '../../hooks/useApiCache';
import ToastNotification from '../../utils/toastNotification';
import {
  createPropertyFormData,
  createPropertyPayload,
  getDistrictOptions,
  PropertyFormAgentOption,
  PropertyFormData,
  PropertyFormFields,
  PropertyFormProjectOption,
  validatePropertyForm,
} from './PropertyFormFields';

interface EditPropertyDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (updatedProperty?: Property) => void;
  property: Property | null;
}

const EditPropertyDialog: React.FC<EditPropertyDialogProps> = ({
  open,
  onClose,
  onSuccess,
  property,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;
  const currentAgentId = user?.agentProfile?.id || '';
  const updatePropertyMutation = useUpdateProperty();
  const { data: rawAgents = [] } = useAgents();
  const { data: rawProjects = [] } = useProjects();
  const agents = rawAgents as PropertyFormAgentOption[];
  const projects = rawProjects as PropertyFormProjectOption[];
  const [formData, setFormData] = useState<PropertyFormData>(createPropertyFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);

  const handleLocationChange = (lat: number, lng: number, googleMapsLink?: string) => {
    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng, googleMapsLink: googleMapsLink || prev.googleMapsLink }));
  };

  useEffect(() => {
    if (open && property) {
      const populated = createPropertyFormData(property);
      if (!isSuperAdmin && currentAgentId) {
        populated.agentId = currentAgentId;
      }
      setFormData(populated);
      setDistrictOptions(getDistrictOptions(populated.governorate));
      setUploadedImages(property.images || []);
    }
  }, [open, property, isSuperAdmin, currentAgentId]);

  const handleChange = (field: keyof PropertyFormData, value: any) => {
    if (field === 'governorate') {
      setDistrictOptions(getDistrictOptions(value as string));
      setFormData((prev) => ({ ...prev, governorate: value as string, district: '' }));
      setErrors((prev) => ({ ...prev, governorate: '' }));
      return;
    }
    if (field === 'propertyType') {
      setFormData((prev) => ({
        ...prev, propertyType: value as PropertyType,
        warehouseType: '', warehouseFloat: '', warehouseAccessType: '',
        vehicleType: '', kitchen: '', rooms: '', landType: '', zone: '', landSize: '',
      }));
      setErrors((prev) => ({ ...prev, propertyType: '' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    setUploadError('');
    try {
      const results = await Promise.all(Array.from(files).map((file) => uploadImageToImgBB(file)));
      const imageUrls = results.map((r) => r.url);
      setUploadedImages((prev) => [...prev, ...imageUrls]);
      ToastNotification.success(`${imageUrls.length} image(s) uploaded successfully!`);
    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload images');
      ToastNotification.error(error.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };


  const handleSubmit = async () => {
    if (!property) return;
    const validationErrors = validatePropertyForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      ToastNotification.warning(Object.values(validationErrors)[0] || 'Please fill in all required fields correctly');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = createPropertyPayload(
        isSuperAdmin ? formData : { ...formData, agentId: currentAgentId },
        uploadedImages,
      );
      if (!isSuperAdmin) {
        delete (payload as any).status;
      }
      await updatePropertyMutation.mutateAsync({ id: property.id, data: payload });
      ToastNotification.success('Property updated successfully!');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error updating property:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update property';
      ToastNotification.error(errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setUploadError('');
    onClose();
  };

  return (
    <FormDialog
      open={open}
      title="Edit Property"
      subtitle="Update property information"
      onClose={handleClose}
      onSubmit={handleSubmit}
      maxWidth="md"
      submitText="Update Property"
      isSubmitting={isSubmitting}
      disableSubmit={isSubmitting}
    >
      <PropertyFormFields
        formData={formData}
        errors={errors}
        districtOptions={districtOptions}
        agents={agents}
        projects={projects}
        uploadedImages={uploadedImages}
        uploadError={uploadError}
        uploadingImages={uploadingImages}
        canEditAgentAssignment={isSuperAdmin}
        canEditStatusSelection={isSuperAdmin}
        onChange={handleChange}
        onProjectSelect={(ownerName, ownerPhone) => {
          setFormData((prev) => ({ ...prev, ownerName, ownerPhone }));
        }}
        onLocationChange={handleLocationChange}
        onImageUpload={handleImageUpload}
        onRemoveImage={handleRemoveImage}
        onClearUploadError={() => setUploadError('')}
      />
      {errors.submit && (
        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormHelperText error>{errors.submit}</FormHelperText>
          </Grid>
        </Grid>
      )}
    </FormDialog>
  );
};

export default EditPropertyDialog;
